// import library
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction'; // for selectable events

// import files
import moment from "moment";
import EventDetailModal from "./EventDetailModal";
import EventRescheduleModal from "./EventRescheduleModal";
import EventCompleteModal from "./EventCompleteModal";
import EventCancelModal from "./EventCancelModal";
import EventCopyModal from "./EventCopyModal";
import FullCalendar from "@fullcalendar/react";
import { env } from "../../../env";
import CartLoaderIcon from "../../Common/Icon/CartLoader";

export default function CalendarMapView({viewType, markers, draggable, radius, sendViewTypeFromChild}) {
    // variables
    const markerRef = useRef();
    const { t } = useTranslation();
    const calendarRef = useRef(null);
    const [localViewType, setLocalViewType] = useState(viewType);
    const baseUrl = env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const position = ([38.17058503265006, 23.965004389531703]);
    const [showViewModal, setShowViewModal] = useState(false);
    const [eventDetails, setEventDetails] = useState({});
    const [showCompleteViewModal, setShowCompleteViewModal] = useState(false);
    const [showRescheduleViewModal, setShowRescheduleViewModal] = useState(false);
    const [showCancelViewModal, setShowCancelViewModal] = useState(false);
    const [showCopyViewModal, setShowCopyViewModal] = useState(false);

    const [currentDate, setCurrentDate] = useState('');
    const [events, setEvents] = useState([]);
    const [appointmentTypeList, setAppointmentTypeList] = useState([]);
    const [kindOfAppointmentList, setKindOfAppointmentList] = useState([]);
    const [isCalendarLoader, setIsCalendarLoader] = useState(false);
    const [cancelReasonList, setCancelReasonList] = useState([]);

    useEffect(() => {
        if (viewType !== localViewType) {
            setLocalViewType(viewType);
        }
    }, [viewType]);

    useEffect(() => {
        const fetchData = async () => {
            await getAppointmentTypeList(baseUrl, token);
            await getKindOfAppointmentList(baseUrl, token);
            await getCancelReasonList(baseUrl, token);
        };
        fetchData();
    }, [baseUrl, token]);

    useEffect(() => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.changeView(localViewType);

            // Get the current view title and set it in state
            const currentMonth = calendarApi.view.title;
            setCurrentDate(currentMonth);
            const filteredAppointments = filterAppointmentsByDate(markers, currentMonth);
            // console.log(filteredAppointments)
            setEvents(filteredAppointments)
        }
    }, [localViewType, markers]);
      
    const goToToday = () => {
        if (calendarRef.current) {
            calendarRef.current.getApi().today();
        }
    };
    const goToPrev = () => {
        if (calendarRef.current) {
            calendarRef.current.getApi().prev();
        }
    };
    const goToNext = () => {
        if (calendarRef.current) {
            calendarRef.current.getApi().next();
        }
    };
    const getAppointmentTypeList = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/ClientAppointments/GetAppointmentTypeList`,{ headers })
        .then((response) => {
            setAppointmentTypeList(response.data);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getKindOfAppointmentList = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/ClientAppointments/GetKindOfAppointmentList`,{ headers })
        .then((response) => {
            setKindOfAppointmentList(response.data);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getCancelReasonList = async (url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Schedule/GetAppointmentCancelReasonList`,{ headers })
        .then((response) => {
            setCancelReasonList(response.data);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const filterAppointmentsByDate = (appointments, filterDate) => {
        if (!filterDate) return appointments; // No filter applied
    
        const parsedDate = parseFilterDate(filterDate);
        return appointments.filter(event => {
            const eventDate = new Date(event.start);
            
            if (parsedDate.type === "specific") {
                return eventDate.toDateString() === parsedDate.date.toDateString();
            } 
            
            if (parsedDate.type === "range") {
                return eventDate >= parsedDate.startDate && eventDate <= parsedDate.endDate;
            } 
            
            if (parsedDate.type === "month") {
                return eventDate.getFullYear() === parsedDate.year && eventDate.getMonth() === parsedDate.month+1;
            }
    
            return false;
        });
    };
    const parseFilterDate = (filterDate) => {
        const rangeMatch = filterDate.match(/(\w+)\s(\d{1,2})\s–\s(\d{1,2}),\s(\d{4})/);
        const specificMatch = filterDate.match(/(\w+)\s(\d{1,2}),\s(\d{4})/);
        const monthMatch = filterDate.match(/(\w+)\s(\d{4})/);
    
        if (rangeMatch) {
            const [, month, startDay, endDay, year] = rangeMatch;
            return {
                type: "range",
                startDate: new Date(`${month} ${startDay}, ${year}`),
                endDate: new Date(`${month} ${endDay}, ${year}`)
            };
        } 
        
        if (specificMatch) {
            const [, month, day, year] = specificMatch;
            return {
                type: "specific",
                date: new Date(`${month} ${day}, ${year}`)
            };
        } 
        
        if (monthMatch) {
            const [, month, year] = monthMatch;
            return {
                type: "month",
                month: new Date(`${month} 1, ${year}`).getMonth(),
                year: parseInt(year, 10)
            };
        }
    
        return null;
    };
    const updateTitle = (arg) => {
        const currentMonth = arg.view.title;
        setCurrentDate(currentMonth);
        const filteredAppointments = filterAppointmentsByDate(markers, currentMonth);
        setEvents(filteredAppointments)
    };
    const handleViewToggle = (view) => {
        setLocalViewType(view);
        sendViewTypeFromChild(view);
        if (calendarRef.current) {
            calendarRef.current.getApi().changeView(view);
        }
    };
    const getCustomIcon = (status) => {
        let color = '';
        
        switch (status) {
            case 'completed':
                color = '#1BC5BD';
                break;
            case 'pending':
                color = '#FFA800';
                break;
            case 'cancelled':
                color = '#F64E60';
                break;
            default:
                color = '#0000FF';
        }

        const iconSvg = `
        <svg width="17" height="24" viewBox="0 0 17 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.27427 12.8429L7.77441 23.7218C7.87654 23.8929 8.06059 23.9985 8.25988 24H8.26484C8.46223 24 8.6459 23.8979 8.75031 23.7302L15.5164 12.8513C15.5332 12.8242 15.5477 12.796 15.5591 12.7675C16.3681 11.4463 16.7949 9.93732 16.7949 8.39669C16.7949 3.76679 13.0273 0 8.3963 0C3.76679 0 0 3.76679 0 8.39669C0 9.9377 0.427169 11.4471 1.23654 12.7686C1.24721 12.7941 1.25979 12.8189 1.27427 12.8429Z" fill="${color}"/>
            <path d="M4 6.87988V7.19988C4 8.09588 4.704 8.79988 5.6 8.79988C6.112 8.79988 6.592 8.54388 6.88 8.15988C7.168 8.54388 7.648 8.79988 8.16 8.79988C8.672 8.79988 9.152 8.54388 9.44 8.15988C9.728 8.54388 10.208 8.79988 10.72 8.79988C11.616 8.79988 12.32 8.09588 12.32 7.19988V6.87988H4Z" fill="white"/>
            <path d="M11.3281 4.192C11.2961 4.064 11.1681 4 11.0401 4H5.28007C5.15207 4 5.02407 4.064 4.99207 4.192L4.09607 6.24H12.1921L11.3281 4.192Z" fill="white"/>
            <path d="M4.31995 9.02393V11.6799C4.31995 11.8719 4.44795 11.9999 4.63995 11.9999H6.87995V9.02393C6.49595 9.31193 6.07995 9.43993 5.59995 9.43993C5.11995 9.43993 4.67195 9.27993 4.31995 9.02393ZM6.01595 9.85593C6.04795 9.82393 6.07995 9.79193 6.11195 9.79193C6.23995 9.72793 6.36795 9.75993 6.46395 9.85593C6.49595 9.88793 6.52795 9.91993 6.52795 9.95193C6.52795 9.98393 6.55995 10.0479 6.55995 10.0799C6.55995 10.1119 6.55995 10.1759 6.52795 10.2079C6.49595 10.2399 6.49595 10.2719 6.46395 10.3039C6.43195 10.3359 6.39995 10.3679 6.36795 10.3679C6.33595 10.3679 6.27195 10.3999 6.23995 10.3999C6.14395 10.3999 6.07995 10.3679 6.01595 10.3039C5.98395 10.2719 5.95195 10.2399 5.95195 10.2079C5.91995 10.1759 5.91995 10.1119 5.91995 10.0799C5.91995 9.98393 5.95195 9.91993 6.01595 9.85593Z" fill="white"/>
            <path d="M9.44002 9.02393C9.05602 9.27993 8.64002 9.43993 8.16002 9.43993C7.93602 9.43993 7.71202 9.40793 7.52002 9.34393V11.9999H11.68C11.872 11.9999 12 11.8719 12 11.6799V9.02393C11.648 9.27993 11.2 9.43993 10.72 9.43993C10.24 9.43993 9.82402 9.31193 9.44002 9.02393Z" fill="white"/>
        </svg>`;

        return new L.Icon({
            iconUrl: "data:image/svg+xml," + encodeURIComponent(iconSvg),
            iconSize: [17, 24],
            iconAnchor: [8, 24],
            popupAnchor: [0, -24],
        });
    };
    const handleShowModal = (data) => {
        setEventDetails(data);
        setShowViewModal(true);
    };
    const handleModalData = (value) => {
        setEventDetails(value.data);
        setShowViewModal(false);
        if(value.type === 'complete') {
            setShowCompleteViewModal(true);
        } else if (value.type === 'reschedule') {
            setShowRescheduleViewModal(true);
        } else if (value.type === 'copy') {
            setShowCopyViewModal(true);
        } else if (value.type === 'cancel') {
            setShowCancelViewModal(true);
        }
    };
    const handleEventChildFromData = (data) => {
        setIsCalendarLoader(true);
        if(data === "reschedule") {
            setShowRescheduleViewModal(false);
        } else if(data === "cancel") {
            setShowCancelViewModal(false);
        } else if (data === "complete") {
            setShowCompleteViewModal(false);
        } else if (data === "copy") {
            setShowCopyViewModal(false);
        }

        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        axios.get(baseUrl+`/api/ClientAppointments/GetCustomerAppointments`,{ headers })
        .then((response) => {
            const formattedAppointments = processAppointments(response.data);
            setEvents(formattedAppointments);
            setIsCalendarLoader(false)
        }).catch((error) => {
            console.log(error);
            setIsCalendarLoader(false)
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const processAppointments = (data) => {
        return data.map(appointment => {
            // Construct start and end times
            const start = `${appointment.appointmentdate}T${appointment.appointmentfromtime}`;
            const end = `${appointment.appointmentdate}T${appointment.appointmenttotime}`;
            const geocode = [appointment.latGPS, appointment.longGPS];
            
            // Determine status and colors
            let status, borderColor, backgroundColor;
    
            if (appointment.iscomplete) {
                status = "completed";
                borderColor = 'border-cal-dblue';
                backgroundColor = 'bg-cal-lblue';
            } else if (appointment.iscancel) {
                status = "cancelled";
                borderColor = 'border-cal-dpink';
                backgroundColor = 'bg-cal-lpink';
            } else {
                status = "pending";
                borderColor = 'border-cal-dorange';
                backgroundColor = 'bg-cal-lorange';
            }
    
            return {
                ...appointment,
                start,
                end,
                geocode,
                status,
                borderColor,
                backgroundColor
            };
        });
    };

    return (
        <div className="relative">
            <div className='px-2 py-2 bg-white rounded-xl'>
                <div className="px-2 sm:px-0 flex justify-between items-center mb-4">
                    <div className="flex space-x-2">
                        <button onClick={goToPrev} className="sm:px-4 sm:py-2 sm:border sm:border-background-normal sm:rounded-lg">
                            <img src="../../assets/icons/table/left-arrow-active.svg" alt='arrow' />
                        </button>
                        <button onClick={goToNext} className="hidden sm:block px-4 py-2 border border-background-normal rounded-lg">
                            <img src="../../assets/icons/table/right-arrow-active.svg" alt='arrow' />
                        </button>
                        <button onClick={goToToday} className="hidden sm:block px-4 py-2 border border-background-normal rounded-lg text-light-400 capitalize">
                            { t('today') }
                        </button>
                    </div>
                    <div className="text-sm sm:text-xl font-bold text-blue-normal bg-blue-light rounded-xl py-2 px-6">{currentDate}</div>
                    <div className="flex space-x-2">
                        <button onClick={() => handleViewToggle('dayGridMonth')} className={(viewType === 'dayGridMonth' ? 'bg-gray-dark3' : 'bg-white') + " sm:px-3 sm:py-2 sm:border sm:border-background-normal sm:rounded-lg"}>
                            <img src="../../assets/icons/profile/calendar/calendar.svg" alt='calendar' />
                        </button>
                    </div>
                    <button onClick={goToNext} className="block sm:hidden">
                        <img src="../../assets/icons/table/right-arrow-active.svg" alt='arrow' />
                    </button>
                </div>
                <div className="hidden">
                    <FullCalendar ref={calendarRef} plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} initialView={localViewType} datesSet={updateTitle}
                        views={{
                            '5Day': {
                                type: 'timeGrid',
                                duration: { days: 5 },
                                buttonText: '5 Days'
                            }
                        }}
                    />
                </div>
                <div className="relative">
                    {isCalendarLoader &&
                        <div className="absolute w-full h-full bg-blue-100 z-40 opacity-50">
                            <CartLoaderIcon /> 
                        </div>
                    }
                    <MapContainer center={position} zoom={5} style={{ height: "75vh", width: "100%" }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {events.length > 0 ? events.map((marker, index) => (
                            <Marker 
                                key={index} 
                                position={marker.geocode} 
                                draggable={draggable}
                                ref={markerRef} 
                                icon={getCustomIcon(marker.status)}
                                eventHandlers={{
                                    click: () => handleShowModal(marker)
                                }}
                            >
                            </Marker>
                        )) : null}
                    </MapContainer>
                </div>
            </div>
            
            {showViewModal && (
                <EventDetailModal
                    eventDetails={eventDetails}
                    setShowViewModal={setShowViewModal}
                    sendModalData={handleModalData}
                />
            )}
            {showCompleteViewModal && (
                <EventCompleteModal
                    eventDetails={eventDetails}
                    setShowCompleteViewModal={setShowCompleteViewModal}
                    sendEventChildFromData={handleEventChildFromData}
                />
            )}
            {showRescheduleViewModal && (
                <EventRescheduleModal
                    eventDetails={eventDetails}
                    setShowRescheduleViewModal={setShowRescheduleViewModal}
                    kindOfAppointmentList={kindOfAppointmentList}
                    appointmentTypeList={appointmentTypeList}
                    sendEventChildFromData={handleEventChildFromData}
                />
            )}
            {showCopyViewModal && (
                <EventCopyModal
                    eventDetails={eventDetails}
                    setShowCopyViewModal={setShowCopyViewModal}
                    kindOfAppointmentList={kindOfAppointmentList}
                    appointmentTypeList={appointmentTypeList}
                    sendEventChildFromData={handleEventChildFromData}
                />
            )}
            {showCancelViewModal && (
                <EventCancelModal
                    eventDetails={eventDetails}
                    setShowCancelViewModal={setShowCancelViewModal}
                    cancelReasonList={cancelReasonList}
                    sendEventChildFromData={handleEventChildFromData}
                />
            )}
        </div>
    );
}
